/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import EnforceIcon from "../../../assets/icons/contract/toggleable/IconEnforce";
import DefendIcon from "../../../assets/icons/contract/toggleable/IconDefend";
import AdvisoryIcon from "../../../assets/icons/contract/toggleable/IconQuestionmarkBubble";
import Person from "../Common/Person/Person";
import _includes from "lodash/includes";
import Address from "../Common/Address/Address";
import PersonIcon from "../../../assets/icons/provision/toggleable/IconPerson";
import FamilyIcon from "../../../assets/icons/generic/toggleable/IconFamilyDetailed";
import ContractBaseEnforceClaim from "./ContractBaseEnforceClaim";
import ContractBaseDefendClaim from "./ContractBaseDefendClaim";
import { dateFieldDefault, isPhoneNumber, minStringLength, pastOrSameDateDefault, requiredValidator } from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorDateField from "../../FormFields/ValidatorDateField";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import AdditionalClientsForm from "../Common/AdditionalClientsForm/AdditionalClientsForm";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import wizardStyle from "../wizardStyle";
import { Box } from "@mui/material";
import QuestionMarkIcon from "../../../assets/icons/common/toggleable/IconQuestionmark";
import CoinsuredClient from "../AbstractLitigationCase/CoinsuredClient";

type ContractBaseProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  hideCaseCategories: boolean;
  alternativeHeadline?: string;
};

export default function ContractBase({ submitProps, product, hideCaseCategories = false, alternativeHeadline }: ContractBaseProps) {
  const { values, handleSubmit, handleChange } = useForm();

  useEffect(() => {
    if (values.legalRepresentativeType === "me") {
      handleChange({
        target: {
          name: "clientLegalRepresentative.gender",
          value: values.customer.person.gender,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.givenName",
          value: values.customer.person.givenName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.familyName",
          value: values.customer.person.familyName,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.streetAddress",
          value: values.customer.person.residenceAddress.streetAddress,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.postalCode",
          value: values.customer.person.residenceAddress.postalCode,
          type: "text",
        },
      });
      handleChange({
        target: {
          name: "clientLegalRepresentative.residenceAddress.addressLocality",
          value: values.customer.person.residenceAddress.addressLocality,
          type: "text",
        },
      });
      return;
    }
    handleChange({
      target: {
        name: "clientLegalRepresentative.gender",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.givenName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.familyName",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.streetAddress",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.postalCode",
        value: "",
        type: "text",
      },
    });
    handleChange({
      target: {
        name: "clientLegalRepresentative.residenceAddress.addressLocality",
        value: "",
        type: "text",
      },
    });
  }, [values.legalRepresentativeType]);

  useEffect(() => {
    if (values.affectedPartyType !== "coinsured") {
      handleChange({
        target: {
          name: "client.gender",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.givenName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.familyName",
          value: "",
        },
      });
      handleChange({
        target: {
          name: "client.birthDate",
          value: null,
        },
      });
      handleChange({
        target: {
          name: "clientWasMinorAtContractDate",
          value: null,
        },
      });
      return;
    }
  }, [values.affectedPartyType]);

  useEffect(() => {
    if (values.affectedPartyType !== "customer" || values.howManyClients === "oneClient") {
      handleChange({
        target: {
          name: "additionalClients",
          value: [],
        },
      });
      return;
    }
    if (values.howManyClients === "twoClients" && !values.additionalClients?.length) {
      handleChange({
        target: {
          name: "additionalClients",
          value: product.additionalClients?.length
            ? product.additionalClients
            : [
                {
                  givenName: "",
                  familyName: "",
                  gender: "",
                },
              ],
        },
      });
    }
  }, [values.howManyClients, values.affectedPartyType]);

  const getCaseCategoryOptions = () => {
    let options = [
      {
        labelText: "Ich möchte eine Forderung durchsetzen",
        labelIcon: <EnforceIcon />,
        labelIconActive: <EnforceIcon active />,
        value: "enforceClaim",
        optionHintText:
          "Der Vertrag wurde nicht wie vereinbart erfüllt und ich möchte dies nun einfordern oder Geld zurück verlangen. Beispiele: Bei einem Kauf wurde die Ware defekt oder gar nicht geliefert. | Bei einem Verkauf wurde der Kaufpreis nicht gezahlt.",
      },
      {
        labelText: "Ich möchte mich gegen eine Forderung wehren",
        labelIcon: <DefendIcon />,
        labelIconActive: <DefendIcon active />,
        value: "defendClaim",
        optionHintText:
          "Von mir wird unberechtigterweise etwas eingefordert und ich möchte mich dagegen wehren. Beispiele: Ich bin von einem Vertrag zurück getreten und trotzdem wird Geld verlangt. | Ich habe nie einen Vertrag geschlossen, soll jetzt aber etwas zahlen. ",
      },
    ];
    if (product.categoryChange === null || product.categoryChange.previousCaseCategory !== "advisory") {
      options = [
        {
          labelText: "Ich benötige zunächst nur eine Erstberatung",
          labelIcon: <AdvisoryIcon />,
          labelIconActive: <AdvisoryIcon active />,
          value: "advisory",
          optionHintText:
            "Ich möchte nichts weiter veranlassen, sondern habe nur eine kurze Frage im Bereich Vertragsrecht und benötige hierfür eine Erstberatung.",
        },
        ...options,
      ];
    }
    return options;
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ihr Vertrag
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>{alternativeHeadline !== null ? alternativeHeadline : "Allgemeines zu Ihrem Vertrag"}</strong>
              </FormLegend>
            </Grid>
            {!hideCaseCategories && (
              <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                <Group
                  type="radio"
                  name="caseCategory"
                  question="Wie können wir Ihnen weiterhelfen?"
                  options={getCaseCategoryOptions()}
                  validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                />
              </Grid>
            )}
            <Person path="customer.person" headline={"Wie heißen Sie?"} showDiverseGenderOption />
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && <Address path="customer.person.residenceAddress" headline={"Wo wohnen Sie?"} />}
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>Wie können wir Sie erreichen?</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name="customer.telephone"
                label="Ihre Telefonnummer"
                autoComplete="tel"
                validators={[
                  requiredValidator("Bitte geben Sie Ihre Telefonnummer an"),
                  {
                    validator: minStringLength,
                    params: [7],
                    message: "Ihre Telefonnummer ist zu kurz",
                  },
                  { validator: isPhoneNumber, message: "Ihre Telefonnummer enthält ungültige Zeichen" },
                ]}
              />
            </Grid>
            {_includes(["enforceClaim", "defendClaim"], values.caseCategory) && (
              <>
                <Grid size={12}>
                  <Group
                    type="radio"
                    name="affectedPartyType"
                    question={"Wer hat den Vertrag geschlossen, um den es bei Ihrem Anliegen geht?"}
                    options={[
                      {
                        labelText: "Ich habe den Vertrag selbst abgeschlossen",
                        labelIcon: <PersonIcon />,
                        labelIconActive: <PersonIcon active />,
                        value: "customer",
                      },
                      {
                        labelText: "Der Vertrag wurde von jemand anderem abgeschlossen, der mir bekannt ist - zum Beispiel ein Familienmitglied",
                        labelIcon: <FamilyIcon />,
                        labelIconActive: <FamilyIcon active />,
                        value: "coinsured",
                      },
                      {
                        labelText: "Ich weiß nicht, wer den Vertrag abgeschlossen hat",
                        labelIcon: <QuestionMarkIcon />,
                        labelIconActive: <QuestionMarkIcon active />,
                        value: "unknown",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>

                {values.affectedPartyType === "customer" && <AdditionalClientsForm product={product} />}
                {values.affectedPartyType === "coinsured" && <CoinsuredClient product={product} />}
                {values.affectedPartyType === "unknown" && (
                  <HighlightBox>
                    Auch wenn Ihnen nicht bekannt ist, wer den Vertrag geschlossen hat, machen Sie bitte in den folgenden Schritten alle Angaben so, als ob Sie
                    den Vertrag geschlossen hätten, damit uns möglichst viele Informationen zum Fall vorliegen. Stören Sie sich dabei nicht an einzelnen
                    Formulierungen: Sie haben am Ende des Formulars die Möglichkeit, Ihren Fall frei und in eigenen Worten zu schildern.
                  </HighlightBox>
                )}
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>
                    Wann wurde der Vertrag abgeschlossen bzw. die Ware/Leistung gekauft? Wenn Sie den Tag nicht genau benennen können, oder der Meinung sind,
                    dass gar kein Vertrag zustande gekommen ist, geben Sie einfach ein ungefähres Datum ein.
                  </FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorDateField
                    name="contractDate"
                    label="Datum des Vertrags"
                    validators={[requiredValidator("Bitte geben Sie das Vertragsdatum an"), dateFieldDefault, pastOrSameDateDefault]}
                  />
                </Grid>
              </>
            )}
            {values.caseCategory === "enforceClaim" && <ContractBaseEnforceClaim product={product} />}
            {values.caseCategory === "defendClaim" && <ContractBaseDefendClaim product={product} />}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
